/**
 * Pure helpers for Risk Analyst thread memory, classify slots, and follow-up clicks.
 * No network. Keyword routing stays the authority; these only fill a miss.
 */
import {
  categoryFromRoleAsk,
  categoryKeyForPhrase,
  extractSupplierListFilters,
  highestRiskScope,
  isAlertsAsk,
  isWhatsNewAsk,
  matchListTableIntent,
  subcategoryKeyForPhrase,
} from './earlySignalsDbIntent'
import { isRefineLastTableAsk } from './riskAnalystSession'
import {
  bareSupplierNameCandidate,
  DIMENSION_KEYS,
  extractRiskDimensionKey,
  extractSupplierNameCandidate,
} from './supplierRiskRatingsTable'

export const CONFIDENCE_MIN = 0.55

export type ThreadMemory = {
  lastSupplier: string | null
  lastCategory: string | null
}

export function emptyThreadMemory(): ThreadMemory {
  return { lastSupplier: null, lastCategory: null }
}

/** Below this, or an explicit fallthrough, the classifier does not pick a tool. */
export function shouldDispatchClassification(confidence: number, intent: string): boolean {
  if (!(confidence >= CONFIDENCE_MIN)) return false
  if (!intent || intent === 'fallthrough') return false
  return true
}

/** Model supplier name is used only when the question did not already name one. */
export function supplierNameForDispatch(userText: string, modelName: string | null): string | null {
  const fromQuestion =
    extractSupplierNameCandidate(userText) || bareSupplierNameCandidate(userText)
  if (fromQuestion) return fromQuestion
  const model = modelName?.trim() || ''
  return model || null
}

export function acceptCategorySlot(raw: string | null | undefined): string | null {
  if (!raw?.trim()) return null
  return categoryKeyForPhrase(raw.trim())
}

export function acceptSubcategorySlot(raw: string | null | undefined): string | null {
  if (!raw?.trim()) return null
  return subcategoryKeyForPhrase(raw.trim())
}

export function acceptDimensionSlot(raw: string | null | undefined): string | null {
  if (!raw?.trim()) return null
  const text = raw.trim()
  const key = text.toLowerCase().replace(/\s+/g, '_')
  const known = DIMENSION_KEYS.find((d) => d.key === key || d.label.toLowerCase() === text.toLowerCase())
  if (known) return known.key
  return extractRiskDimensionKey(text)
}

/** Category already present in the question. A model slot must not replace it. */
export function categoryNamedInQuestion(userText: string): string | null {
  const role = categoryFromRoleAsk(userText)
  if (role && categoryKeyForPhrase(role)) return categoryKeyForPhrase(role)
  const scope = highestRiskScope(userText)
  if (scope) {
    const cat = categoryKeyForPhrase(scope)
    if (cat) return cat
    const sub = subcategoryKeyForPhrase(scope)
    if (sub) return sub
  }
  const filters = extractSupplierListFilters(userText)
  if (filters.categoryHint) {
    const cat = categoryKeyForPhrase(filters.categoryHint)
    if (cat) return cat
  }
  if (filters.subCategoryHint) {
    const sub = subcategoryKeyForPhrase(filters.subCategoryHint)
    if (sub) return sub
  }
  return null
}

/**
 * Category for a tool that needs one. Question text wins. Unknown model slots are dropped.
 * A subcategory slot is used only when the question did not name a category.
 */
export function categoryForDispatch(
  userText: string,
  modelCategory: string | null | undefined,
  modelSubcategory: string | null | undefined
): string | null {
  const fromQuestion = categoryNamedInQuestion(userText)
  if (fromQuestion) return fromQuestion
  return acceptCategorySlot(modelCategory) || acceptSubcategorySlot(modelSubcategory)
}

function questionNamesSupplier(text: string): boolean {
  if (extractSupplierNameCandidate(text) || bareSupplierNameCandidate(text)) return true
  const scope = highestRiskScope(text)
  if (!scope) return false
  if (categoryKeyForPhrase(scope) || subcategoryKeyForPhrase(scope)) return false
  return true
}

function questionNamesCategory(text: string): boolean {
  return categoryNamedInQuestion(text) != null
}

function mustNotInherit(text: string): boolean {
  const t = text.toLowerCase()
  if (isBareHighestRiskFollowUp(text)) return false
  if (isWhatsNewAsk(text) || isAlertsAsk(t) || isRefineLastTableAsk(text)) return true
  if (matchListTableIntent(text)) return true
  if (/\bspend\b/.test(t)) return true
  if (/\bmitigation/.test(t)) return true
  if (/\b(how many|count)\b/.test(t)) return true
  if (/\bsuppliers\b/.test(t)) return true
  return false
}

function isBareHighestRiskFollowUp(text: string): boolean {
  const t = text.toLowerCase().replace(/\s+/g, ' ').trim()
  if (!/\b(highest|top)\b/.test(t) || !/\brisk\b/.test(t)) return false
  if (/\bsuppliers\b/.test(t)) return false
  return true
}

/**
 * Attach the last supplier only to a highest-risk follow-up that names neither
 * a supplier nor a category. Lists, alerts, and a new name stay as typed.
 */
export function inheritThreadFocus(text: string, memory: ThreadMemory): string {
  const supplier = memory.lastSupplier?.trim() || ''
  if (!supplier) return text
  if (mustNotInherit(text)) return text
  if (questionNamesSupplier(text) || questionNamesCategory(text)) return text
  if (!isBareHighestRiskFollowUp(text)) return text
  const base = text.replace(/[?.!\s]+$/g, '').trim()
  return `${base} in ${supplier}`
}

/** Supplier, category, score, alert, mitigation, or spend — not an open chat. */
export function isPlatformRiskQuestion(text: string): boolean {
  const t = text.toLowerCase()
  if (
    /\b(supplier|suppliers|alert|alerts|mitigation|mitigations|spend|score|scores|rating|ratings|risk|category|categories|sub-?category)\b/.test(
      t
    )
  ) {
    return true
  }
  if (questionNamesSupplier(text) || questionNamesCategory(text)) return true
  if (matchListTableIntent(text)) return true
  return false
}

export function nextQuestionFrom(content: string): string | null {
  const match = content.match(/Next question:\s*[“"]([^”"]+)[”"]/i)
  const question = match?.[1]?.trim()
  return question || null
}

export function bodyWithoutNextQuestion(content: string): string {
  return content.replace(/\n*Next question:\s*[“"][^”"]+[”"]\.?\s*$/i, '').trim()
}

export const PLATFORM_DATA_SENTENCE =
  'I can pull that from platform data if you name a supplier, or ask for active mitigations, alerts, high-risk suppliers, brands, or risk ratings.'
